import React, { memo, useState, useMemo, useRef } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  BellRing,
  Sparkles,
  Zap,
  Plus,
  Trash2,
  ArrowRight,
  ShieldCheck,
  AlertTriangle,
  Phone,
  MessageSquare,
  Mail,
  Webhook,
  Sliders,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Flame,
  Tag,
  Eye,
  Send,
  HelpCircle,
  Copy,
} from 'lucide-react';

export interface IntentRule {
  id: string;
  intentTitle: string;
  matchConditions: string;
  notificationChannel: 'whatsapp' | 'sms' | 'email' | 'webhook';
  alertMessageTemplate?: string;
  urgency: 'high' | 'medium' | 'low';
  isEnabled: boolean;
}

export interface IntentAlertNodeData {
  rules?: IntentRule[];
  silentMode?: boolean;
  [key: string]: unknown;
}

const DEFAULT_SAMPLE_RULES: IntentRule[] = [
  {
    id: 'rule_red_roses',
    intentTitle: '🌹 Red Rose & Romance Inquiries',
    matchConditions: 'asking for red roses, romantic flower bouquet, Valentine roses, price of red roses',
    notificationChannel: 'whatsapp',
    alertMessageTemplate: '🌹 High Intent: Customer is interested in Red Roses bouquet!',
    urgency: 'high',
    isEnabled: true,
  },
  {
    id: 'rule_vip_bulk',
    intentTitle: '💎 VIP Bulk / Wedding Order (> $500)',
    matchConditions: 'wedding decorations, bulk order, high budget, event floral arrangements',
    notificationChannel: 'whatsapp',
    alertMessageTemplate: '💎 VIP Order Alert: Customer inquiring about bulk/wedding flowers!',
    urgency: 'high',
    isEnabled: true,
  },
];

export const IntentAlertNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as IntentAlertNodeData;

  const rules: IntentRule[] = Array.isArray(nodeData.rules) && nodeData.rules.length > 0
    ? nodeData.rules
    : (nodeData.rules === undefined ? DEFAULT_SAMPLE_RULES : []);

  const silentMode = nodeData.silentMode !== false;

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // New Rule Temporary State
  const [newRuleTitle, setNewRuleTitle] = useState('');
  const [newRuleKeywords, setNewRuleKeywords] = useState('');
  const [newRuleChannel, setNewRuleChannel] = useState<'whatsapp' | 'sms' | 'email' | 'webhook'>('whatsapp');
  const [newRuleUrgency, setNewRuleUrgency] = useState<'high' | 'medium' | 'low'>('high');
  const [newRuleMessage, setNewRuleMessage] = useState('');

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);
  const persistNodeDataDirectly = (allUpdates: Partial<IntentAlertNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          rules,
          silentMode,
          ...allUpdates,
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'INTENT_ALERT_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[IntentAlertNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleUpdateField = (field: keyof IntentAlertNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly({ [field]: value });
  };

  const handleUpdateRule = (ruleId: string, updates: Partial<IntentRule>) => {
    const updatedRules = rules.map((r) => (r.id === ruleId ? { ...r, ...updates } : r));
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, rules: updatedRules } } : n))
    );
    persistNodeDataDirectly({ rules: updatedRules });
  };

  const handleDeleteRule = (ruleId: string) => {
    const updatedRules = rules.filter((r) => r.id !== ruleId);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, rules: updatedRules } } : n))
    );
    persistNodeDataDirectly({ rules: updatedRules });
  };

  const handleAddRuleSubmit = () => {
    if (!newRuleTitle.trim() || !newRuleKeywords.trim()) return;
    const newRule: IntentRule = {
      id: `rule_${Date.now()}`,
      intentTitle: newRuleTitle.trim(),
      matchConditions: newRuleKeywords.trim(),
      notificationChannel: newRuleChannel,
      alertMessageTemplate: newRuleMessage.trim() || `🚨 Alert: Client matched intent: ${newRuleTitle.trim()}`,
      urgency: newRuleUrgency,
      isEnabled: true,
    };

    const updatedRules = [...rules, newRule];
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, rules: updatedRules } } : n))
    );
    persistNodeDataDirectly({ rules: updatedRules });

    // Reset Form
    setNewRuleTitle('');
    setNewRuleKeywords('');
    setNewRuleMessage('');
    setIsAddingRule(false);
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#180A12]/98 via-[#0F060B]/98 to-[#080306]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-rose-400 ring-2 ring-rose-400/50 shadow-[0_0_35px_rgba(244,63,94,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-rose-500/40 hover:border-rose-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(244,63,94,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Alerts in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('intent_alerts', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-950 via-slate-900 to-pink-950 border-2 border-rose-400 text-rose-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(244,63,94,0.8)] hover:border-rose-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view live intent telemetry & trigger history"
          >
            <BellRing size={13} className="text-rose-300 animate-pulse" />
            <span>View Alerts in Portal</span>
            <ArrowRight size={13} className="text-rose-400" />
          </button>
        </div>
      )}

      {/* ── Handles: Left Target (Execution from Core) & Right Source (Sensory Context to Core) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-intent"
        className="!bg-rose-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(244,63,94,1)]"
        title="Action execution input from Luna Core Hub"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-intent"
        className="!bg-rose-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(244,63,94,1)]"
        title="Upstream Intent Intelligence output to Luna Core Hub"
      />

      {/* ── Header ── */}
      <div className="p-4 border-b border-rose-500/30 bg-gradient-to-r from-rose-950/80 via-pink-950/50 to-slate-950/80 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-500/20 border border-rose-500/40 flex items-center justify-center text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.3)]">
              <BellRing size={20} className={isNodeActive ? 'animate-bounce text-cyan-300' : 'text-rose-400'} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm text-slate-100 tracking-wide flex items-center gap-1.5">
                  Smart Intent & VIP Alerts
                </h3>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                  AUTONOMOUS
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Silently alerts owner on customer interests & needs
              </p>
            </div>
          </div>

          {/* Status & Wire Indicator */}
          <div className="flex flex-col items-end gap-1">
            <span
              className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                isWired
                  ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
              }`}
            >
              {isWired ? '● ACTIVE' : '○ UNWIRED'}
            </span>
            {saveIndicator && (
              <span className="text-[9px] text-emerald-400 font-mono animate-fade-in">
                {saveIndicator}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* ── Node Body ── */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-rose-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BellRing size={14} className="text-rose-400" />
                <span className="font-bold text-white text-xs">
                  {rules.length} Intent Rules Monitored
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-rose-500/20 hover:bg-rose-500/35 text-rose-200 border border-rose-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-rose-300 font-bold">
                ⚡ {rules.filter(r => r.urgency === 'high').length} High Urgency
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                {silentMode ? 'Silent Mode' : 'Customer Notify'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-rose-950/70 border border-rose-500/30 text-[10px] font-mono text-rose-300">
                Owner Direct Ping
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-4 text-xs">
        {/* Gateway Info Banner */}
        <div className="p-2.5 rounded-xl bg-gradient-to-r from-rose-950/40 via-purple-950/30 to-slate-900/60 border border-rose-500/25 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={13} className="text-rose-400 shrink-0" />
            <span className="text-[10px] text-slate-300 font-sans leading-tight">
              Alerts dispatch silently to owner via connected <strong className="text-rose-300">Action & Integration Node</strong>.
            </span>
          </div>
        </div>

        {/* Active Intent Rules Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold text-slate-300 flex items-center gap-1.5">
              <Sparkles size={12} className="text-pink-400" />
              Monitored Intent Rules ({rules.length})
            </label>
            <button
              type="button"
              onClick={() => setIsAddingRule(!isAddingRule)}
              className="nodrag flex items-center gap-1 text-[10px] text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 px-2 py-0.5 rounded cursor-pointer transition-colors"
            >
              <Plus size={11} />
              <span>Add Intent</span>
            </button>
          </div>

          {/* List of Configured Rules */}
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {rules.map((rule) => {
              const isExpanded = expandedRuleId === rule.id;
              return (
                <div
                  key={rule.id}
                  className={`rounded-xl border transition-all ${
                    rule.isEnabled
                      ? 'bg-slate-900/80 border-rose-500/20 hover:border-rose-500/40'
                      : 'bg-slate-950/60 border-slate-800/80 opacity-60'
                  }`}
                >
                  {/* Rule Header Bar */}
                  <div
                    className="p-2.5 flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedRuleId(isExpanded ? null : rule.id)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          rule.urgency === 'high'
                            ? 'bg-rose-400 shadow-[0_0_6px_rgba(244,63,94,0.8)]'
                            : rule.urgency === 'medium'
                            ? 'bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.8)]'
                            : 'bg-emerald-400 shadow-[0_0_6px_rgba(16,185,129,0.8)]'
                        }`}
                      />
                      <span className="font-semibold text-slate-200 truncate text-[11px]">
                        {rule.intentTitle}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase font-mono px-1.5 py-0.2 rounded bg-slate-800 text-slate-400 border border-slate-700">
                        {rule.notificationChannel}
                      </span>
                      <input
                        type="checkbox"
                        checked={rule.isEnabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleUpdateRule(rule.id, { isEnabled: e.target.checked });
                        }}
                        className="nodrag accent-rose-500 cursor-pointer w-3.5 h-3.5"
                        title="Toggle Intent Rule"
                      />
                      {isExpanded ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded Rule Details */}
                  {isExpanded && (
                    <div className="p-3 pt-0 border-t border-slate-800/80 space-y-2 mt-1">
                      <div className="space-y-1">
                        <span className="text-[10px] text-slate-400 font-mono">Trigger Keywords / Criteria:</span>
                        <input
                          type="text"
                          value={rule.matchConditions}
                          onChange={(e) => handleUpdateRule(rule.id, { matchConditions: e.target.value })}
                          className="nodrag w-full bg-slate-950 border border-slate-800 focus:border-rose-400 rounded px-2 py-1 text-slate-200 text-[10px] focus:outline-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-slate-400 font-mono">Channel:</span>
                          <select
                            value={rule.notificationChannel}
                            onChange={(e) => handleUpdateRule(rule.id, { notificationChannel: e.target.value as any })}
                            className="nodrag w-full bg-slate-950 border border-slate-800 focus:border-rose-400 rounded px-2 py-1 text-slate-200 text-[10px] focus:outline-none"
                          >
                            <option value="whatsapp">📱 WhatsApp</option>
                            <option value="sms">💬 SMS Alert</option>
                            <option value="email">✉️ Email</option>
                            <option value="webhook">🌐 Webhook</option>
                          </select>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 font-mono">Urgency Level:</span>
                          <select
                            value={rule.urgency}
                            onChange={(e) => handleUpdateRule(rule.id, { urgency: e.target.value as any })}
                            className="nodrag w-full bg-slate-950 border border-slate-800 focus:border-rose-400 rounded px-2 py-1 text-slate-200 text-[10px] focus:outline-none"
                          >
                            <option value="high">🚨 High / Instant</option>
                            <option value="medium">⚡ Medium</option>
                            <option value="low">📋 Low / Summary</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-end pt-1">
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="nodrag text-[10px] text-rose-400 hover:text-rose-300 flex items-center gap-1 hover:underline cursor-pointer"
                        >
                          <Trash2 size={11} />
                          <span>Remove Rule</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Rule Form Modal/Card */}
        {isAddingRule && (
          <div className="p-3 rounded-xl bg-gradient-to-br from-rose-950/50 to-slate-900/80 border border-rose-500/30 space-y-2 animate-fade-in">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-rose-300 flex items-center gap-1">
                <Plus size={12} /> Add New Intent Trigger
              </span>
              <button
                type="button"
                onClick={() => setIsAddingRule(false)}
                className="nodrag text-slate-400 hover:text-slate-200 text-[10px]"
              >
                Cancel
              </button>
            </div>

            <input
              type="text"
              value={newRuleTitle}
              onChange={(e) => setNewRuleTitle(e.target.value)}
              placeholder="Intent Title (e.g. 🌹 Red Rose Lovers)"
              className="nodrag w-full bg-slate-900 border border-slate-700 focus:border-rose-400 rounded px-2.5 py-1.5 text-slate-100 text-xs focus:outline-none"
            />

            <textarea
              value={newRuleKeywords}
              onChange={(e) => setNewRuleKeywords(e.target.value)}
              placeholder="Trigger Conditions / Keywords (e.g. looking for red roses, wants romantic bouquet, asks about red flowers)"
              rows={2}
              className="nodrag w-full bg-slate-900 border border-slate-700 focus:border-rose-400 rounded px-2.5 py-1.5 text-slate-100 text-xs focus:outline-none resize-none"
            />

            <div className="grid grid-cols-2 gap-2">
              <select
                value={newRuleChannel}
                onChange={(e) => setNewRuleChannel(e.target.value as any)}
                className="nodrag bg-slate-900 border border-slate-700 focus:border-rose-400 rounded px-2 py-1 text-slate-200 text-[11px] focus:outline-none"
              >
                <option value="whatsapp">📱 WhatsApp Alert</option>
                <option value="sms">💬 SMS Alert</option>
                <option value="email">✉️ Email</option>
                <option value="webhook">🌐 Webhook</option>
              </select>

              <select
                value={newRuleUrgency}
                onChange={(e) => setNewRuleUrgency(e.target.value as any)}
                className="nodrag bg-slate-900 border border-slate-700 focus:border-rose-400 rounded px-2 py-1 text-slate-200 text-[11px] focus:outline-none"
              >
                <option value="high">🚨 High Priority</option>
                <option value="medium">⚡ Medium</option>
                <option value="low">📋 Low</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleAddRuleSubmit}
              disabled={!newRuleTitle.trim() || !newRuleKeywords.trim()}
              className="nodrag w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 disabled:opacity-50 text-white font-bold py-1.5 rounded-lg text-xs transition-all shadow-md active:scale-98 cursor-pointer"
            >
              Save Intent Rule
            </button>
          </div>
        )}

        {/* Silent Execution Toggle */}
        <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-semibold text-slate-300 block">Silent Execution Mode</span>
            <span className="text-[10px] text-slate-500">Alerts owner without notifying customer</span>
          </div>
          <input
            type="checkbox"
            checked={silentMode}
            onChange={(e) => handleUpdateField('silentMode', e.target.checked)}
            className="nodrag accent-rose-500 cursor-pointer w-4 h-4"
          />
        </div>
      </div>
      )}
    </div>
  );
});

export default IntentAlertNode;
